import type { AccountConstraintInput, AccountModel, AccountModelInput, TaiKhoan } from '@/types/schemas';
import type { AccountConstraintRow, NormalizedConstraintResult } from './types';
import { type AccountGroupValue, DEFAULT_ACCOUNT_GROUP } from './constants';
import type { FormValues } from './schemas';

const randomSuffix = () =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export const createConstraintRowId = (seed?: string) => {
  const normalizedSeed = seed?.trim().replace(/\s+/g, '-').toLocaleLowerCase();
  return normalizedSeed ? `constraint-${normalizedSeed}-${randomSuffix()}` : `constraint-${randomSuffix()}`;
};

export const createConstraintRow = (partial?: Partial<AccountConstraintRow>): AccountConstraintRow => ({
  id: partial?.id ?? createConstraintRowId(partial?.uuid ?? partial?.objectName),
  uuid: partial?.uuid,
  account: partial?.account,
  constraintCode: partial?.constraintCode,
  constraintLine: partial?.constraintLine,
  objectName: partial?.objectName ?? '',
  requireInput: Boolean(partial?.requireInput),
  isNew: partial?.isNew
});

const toConstraintRow = (value: unknown): AccountConstraintRow | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const record = value as Record<string, unknown>;

  if (typeof record.ten_doi_tuong === 'string') {
    return createConstraintRow({
      uuid: typeof record.uuid === 'string' ? record.uuid : undefined,
      account: typeof record.account === 'string' ? record.account : undefined,
      constraintCode: typeof record.constraint_code === 'string' ? record.constraint_code : undefined,
      constraintLine: typeof record.constraint_line === 'number' ? record.constraint_line : undefined,
      objectName: record.ten_doi_tuong,
      requireInput: Boolean(record.bat_buoc_nhap)
    });
  }

  if (typeof record.constraint_name === 'string') {
    return createConstraintRow({
      uuid: typeof record.uuid === 'string' ? record.uuid : undefined,
      account: typeof record.account === 'string' ? record.account : undefined,
      constraintCode: typeof record.constraint_code === 'string' ? record.constraint_code : undefined,
      constraintLine: typeof record.constraint_line === 'number' ? record.constraint_line : undefined,
      objectName: record.constraint_name,
      requireInput: Boolean(record.is_mandatory)
    });
  }

  if (typeof record.objectName === 'string') {
    return createConstraintRow({
      uuid: typeof record.uuid === 'string' ? record.uuid : undefined,
      account: typeof record.account === 'string' ? record.account : undefined,
      constraintCode: typeof record.constraintCode === 'string' ? record.constraintCode : undefined,
      constraintLine: typeof record.constraintLine === 'number' ? record.constraintLine : undefined,
      objectName: record.objectName,
      requireInput: Boolean(record.requireInput)
    });
  }

  return null;
};

export const normalizeAccountConstraints = (constraints?: unknown[]): NormalizedConstraintResult => {
  const normalizedRows = [...(constraints ?? [])]
    .sort((a, b) => {
      const lineA =
        a && typeof a === 'object' && typeof (a as { constraint_line?: unknown }).constraint_line === 'number'
          ? ((a as { constraint_line: number }).constraint_line ?? 0)
          : 0;
      const lineB =
        b && typeof b === 'object' && typeof (b as { constraint_line?: unknown }).constraint_line === 'number'
          ? ((b as { constraint_line: number }).constraint_line ?? 0)
          : 0;

      return lineA - lineB;
    })
    .map(toConstraintRow)
    .filter(Boolean) as AccountConstraintRow[];

  return {
    rows: normalizedRows,
    shouldFetchCanonical: false
  };
};

export const resolveAccountGroup = (code?: string | null): AccountGroupValue => {
  const firstChar = code?.trim().charAt(0).toUpperCase();

  switch (firstChar) {
    case '1':
      return 'Tài sản ngắn hạn';
    case '2':
      return 'Tài sản dài hạn';
    case '3':
      return 'Nợ phải trả';
    case '4':
      return 'Vốn chủ sở hữu';
    case '5':
      return 'Doanh thu';
    case '6':
      return 'Chi phí sản xuất, kinh doanh';
    case '7':
      return 'Thu nhập khác';
    case '8':
      return 'Chi phí khác';
    case '9':
      return 'Xác định kết quả kinh doanh';
    case 'N':
      return 'Tài khoản ngoài bảng';
    default:
      return DEFAULT_ACCOUNT_GROUP;
  }
};

export const getParentAccountCode = (account: AccountModel) =>
  account.parent_account_code_data?.code ?? account.parent_account_code ?? '';

export const getParentAccountName = (account: AccountModel) => account.parent_account_code_data?.name ?? '';

export const getDebitBalanceCalcLabel = (account: AccountModel) =>
  account.fx_reval_debit_type === 1 ? 'Phát sinh trong kỳ' : 'Số dư cuối kỳ';

export const getCreditBalanceCalcLabel = (account: AccountModel) =>
  account.fx_reval_credit_type === 1 ? 'Phát sinh trong kỳ' : 'Số dư cuối kỳ';

export const createInitialFormValues = (account?: AccountModel | null, isCopyMode = false): FormValues => {
  if (!account) {
    return {
      code: '',
      name: '',
      parent_account_code: '',
      tk_so_cai: false,
      tk_chi_tiet: false,
      bac_tk: 0,
      currency_code: '',
      ar_ap_tracking_type: 0,
      account_type_code: 'KPL',
      fx_reval_debit_type: 0,
      fx_reval_credit_type: 0,
      account_name_2: '',
      account_name_3: '',
      short_name_1: '',
      short_name_2: '',
      notes: '',
      active: true,
      balance_type: 'debit',
      role: 'in_other'
    };
  }

  return {
    code: isCopyMode ? `${account.code}_copy` : account.code,
    name: account.name,
    parent_account_code: getParentAccountCode(account),
    tk_so_cai: Boolean(account.tk_so_cai),
    tk_chi_tiet: Boolean(account.tk_chi_tiet),
    bac_tk: account.bac_tk ?? 0,
    currency_code: account.currency_code ?? '',
    ar_ap_tracking_type: account.ar_ap_tracking_type ?? 0,
    account_type_code: account.account_type_code ?? 'KPL',
    fx_reval_debit_type: account.fx_reval_debit_type ?? 0,
    fx_reval_credit_type: account.fx_reval_credit_type ?? 0,
    account_name_2: account.account_name_2 ?? '',
    account_name_3: account.account_name_3 ?? '',
    short_name_1: account.short_name_1 ?? '',
    short_name_2: account.short_name_2 ?? '',
    notes: account.notes ?? '',
    active: Boolean(account.active),
    balance_type: account.balance_type ?? 'debit',
    role: String(account.role ?? 'in_other')
  };
};

const buildConstraintPayload = (constraints: AccountConstraintRow[], accountUuid?: string): AccountConstraintInput[] =>
  constraints.map(row => ({
    uuid: row.uuid,
    account: row.account ?? accountUuid,
    constraint_code: row.constraintCode ?? '',
    constraint_line: row.constraintLine ?? 0,
    constraint_name: row.objectName,
    is_mandatory: Boolean(row.requireInput)
  }));

export const buildAccountPayload = (
  values: FormValues,
  parentAccount: TaiKhoan | null,
  constraints: AccountConstraintRow[] = [],
  accountUuid?: string
): AccountModelInput => ({
  code: values.code.trim(),
  name: values.name.trim(),
  role: (values.role || 'in_other') as AccountModelInput['role'],
  balance_type: (values.balance_type || 'debit') as AccountModelInput['balance_type'],
  active: Boolean(values.active),
  account_name_2: values.account_name_2?.trim() || null,
  account_name_3: values.account_name_3?.trim() || null,
  parent_account_code: parentAccount?.uuid ?? null,
  account_group_1: null,
  short_name_1: values.short_name_1?.trim() || null,
  short_name_2: values.short_name_2?.trim() || null,
  is_parent_account: Boolean(values.tk_so_cai),
  tk_so_cai: Boolean(values.tk_so_cai),
  tk_chi_tiet: Boolean(values.tk_chi_tiet),
  bac_tk: values.bac_tk ?? 0,
  currency_code: values.currency_code?.trim() || '',
  ar_ap_tracking_type: values.ar_ap_tracking_type ?? 0,
  account_type_code: values.account_type_code?.trim() || 'KPL',
  fx_reval_debit_type: values.fx_reval_debit_type ?? 0,
  fx_reval_credit_type: values.fx_reval_credit_type ?? 0,
  notes: values.notes?.trim() || null,
  constraints: buildConstraintPayload(constraints, accountUuid)
});
